import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEightD } from '../contexts/EightDContext';
import { useFracas } from '../contexts/FracasContext';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle } from 'lucide-react';
import { EightDReport, UpdateFieldFn } from '../types/eightd.types';
import { EightDHeader } from '../components/eightd/EightDHeader';
import { EightDBasicInfo } from '../components/eightd/EightDBasicInfo';
import { EightDTabNavigation } from '../components/eightd/EightDTabNavigation';
import { EightDTabContent } from '../components/eightd/EightDTabContent';

const EightDWorksheet: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { getReportById, createReport, updateReport } = useEightD();
    const { incidents } = useFracas();

    const getDefaultReport = (): Partial<EightDReport> => {
        return {
            title: '',
            productName: '',
            customerName: '',
            status: 'Draft',
            d0_formation: {
                problemDescription: '',
                emergencyResponseActions: '',
                teamMembers: [],
                teamLeaderId: user?.uid || '',
                teamLeaderName: user?.displayName || user?.email || '',
            },
            d1_problem: {
                problemStatement: '',
                isVsIsNot: {
                    what: { is: '', isNot: '' },
                    where: { is: '', isNot: '' },
                    when: { is: '', isNot: '' },
                    howMuch: { is: '', isNot: '' },
                },
                quantification: {
                    defectRate: 0,
                    affectedUnits: 0,
                    customerImpact: '',
                },
            },
            d2_containment: [],
            d3_rootCause: [],
            d4_correctiveAction: [],
            d5_validation: {
                validationMethod: '',
                validationCriteria: '',
                validationResults: '',
                approved: false,
            },
            d6_preventive: [],
            d7_recognition: {
                teamAccomplishments: '',
                individualContributions: [],
                lessonsLearned: '',
                bestPractices: '',
            },
            d8_closure: {
                closureSummary: '',
                resultsAchieved: '',
                metricsImprovement: {
                    beforeDefectRate: 0,
                    afterDefectRate: 0,
                },
                documentationComplete: false,
                closedBy: '',
                closedByName: '',
            },
            completionStatus: {
                d0: false, d1: false, d2: false, d3: false, d4: false,
                d5: false, d6: false, d7: false, d8: false,
            },
        };
    }

    const [report, setReport] = useState<Partial<EightDReport>>(getDefaultReport());
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (id) {
            const existingReport = getReportById(id);
            if (existingReport) {
                setReport(existingReport);
            }
        }
    }, [id, getReportById]);

    const handleSave = async (signatureData?: any) => {
        if (!report.title?.trim()) {
            setError('Please enter a title');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const reportToSave = { ...report };
            if (signatureData) {
                reportToSave.electronicSignature = signatureData;
                reportToSave.status = 'Closed';
                reportToSave.actualCompletionDate = new Date();
            }

            if (id) {
                await updateReport(id, reportToSave);
            } else {
                const newId = await createReport(reportToSave);
                navigate(`/8d/${newId}`);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save report');
        } finally {
            setSaving(false);
        }
    };

    const updateField: UpdateFieldFn = (path, value) => {
        setReport(prev => {
            const keys = path.split('.');
            const updated = { ...prev };
            let current: any = updated;

            for (let i = 0; i < keys.length - 1; i++) {
                current[keys[i]] = { ...current[keys[i]] };
                current = current[keys[i]];
            }

            current[keys[keys.length - 1]] = value;
            return updated;
        });
    };

    return (
        <div className="p-8 max-w-7xl mx-auto">
            <EightDHeader
                id={id}
                report={report}
                saving={saving}
                handleSave={handleSave}
            />

            {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            <EightDBasicInfo
                report={report}
                updateField={updateField}
                incidents={incidents}
            />

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <EightDTabNavigation
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                />
                <EightDTabContent
                    activeTab={activeTab}
                    report={report}
                    updateField={updateField}
                    setReport={setReport}
                />
            </div>

            {/* Navigation Buttons */}
            <div className="mt-6 flex items-center justify-between">
                <button
                    onClick={() => setActiveTab(Math.max(0, activeTab - 1))}
                    disabled={activeTab === 0}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Previous
                </button>

                <div className="text-sm text-gray-600">
                    Step {activeTab + 1} of 10
                </div>

                <button
                    onClick={() => setActiveTab(Math.min(9, activeTab + 1))}
                    disabled={activeTab === 9}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default EightDWorksheet;
